import {
  Box,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  CommonList,
  ConfirmSelectionButton,
  ListSelectionMode,
  SelectionPageHeader,
} from '../../common/components';
import type { Contact } from '../../../types/contact';
import type { ActivityFilters } from '../../activity/types';
import type { ContactSelectionContext } from '../types';
import { formatContactType, getTypeFilterForMode } from '../types';
import { formatContactFullName } from '../../../utils/contactName';
import { useContactData } from '../useContactData';
import { useContactMultiSelection } from './useContactMultiSelection';

export default function ContactMultiSelectPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const companyId = id ? parseInt(id) : null;

  const selectionContext = location.state as ContactSelectionContext;
  const typeFilter = getTypeFilterForMode(selectionContext?.mode);

  const isClientRole = typeFilter === 'CLIENT';

  const { data: { contacts, loading, error } } = useContactData({ companyId, typeFilter });

  const initialContacts = isClientRole
    ? selectionContext?.activityFilters?.clients as unknown as Contact[]
    : selectionContext?.activityFilters?.providers as unknown as Contact[];

  const {
    data: { selectedContactsMap, limitError },
    actions: { handleToggleContactSelection, handleClearLimitError },
  } = useContactMultiSelection({
    contacts,
    initialContacts,
  });

  const handleConfirmMultiSelection = () => {
    if (selectedContactsMap.size > 0 && selectionContext?.returnPath) {
      const selected = Array.from(selectedContactsMap.values());
      const updatedFilters: ActivityFilters = {
        ...selectionContext.activityFilters,
        ...(isClientRole
          ? { clients: selected as unknown as ActivityFilters['clients'] }
          : { providers: selected as unknown as ActivityFilters['providers'] }),
      } as ActivityFilters;

      navigate(selectionContext.returnPath, {
        state: {
          activityFilters: updatedFilters,
        },
      });
    }
  };

  const selectedCount = selectedContactsMap.size;
  const label = isClientRole ? 'клієнтів' : 'постачальників';

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <SelectionPageHeader
        title={`Вибір ${label}`}
        companyId={companyId}
        returnPath={selectionContext?.returnPath}
      >
        <ConfirmSelectionButton
          onClick={handleConfirmMultiSelection}
          count={selectedCount}
          disabled={selectedCount === 0}
        />
      </SelectionPageHeader>

      <Alert severity="info" sx={{ mb: 2 }}>
        Виберіть {label} для фільтрації активностей (до 5 шт.)
      </Alert>

      {limitError && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          onClose={handleClearLimitError}
        >
          {limitError}
        </Alert>
      )}

      {/* Contact List */}
      {contacts.length === 0 ? (
        <Typography variant="body1" sx={{ mt: 2 }}>
          Контакти не знайдені
        </Typography>
      ) : (
        <CommonList<Contact>
          items={contacts}
          toListItem={(item) => ({
            id: item.id,
            primary: formatContactFullName(item),
            secondary: item.phoneNumber,
            tertiary: formatContactType(item.type),
          })}
          selectionMode={ListSelectionMode.MULTIPLE_SELECTION}
          selectedItemIds={Array.from(selectedContactsMap.keys())}
          onToggleSelect={handleToggleContactSelection}
          emptyMessage="Контакти не знайдено"
        />
      )}
    </Box>
  );
}
